// Deriving directory entry keys from phone numbers.
//
// The published directory is keyed by slow_hash(salt ‖ E.164), not by the number
// itself. The slowness is deliberate: a plain SHA-256 over E.164 numbers is
// enumerable in minutes, while cost parameters in the tens of milliseconds multiply
// a full sweep into something nobody does casually. The client derives one key per
// lookup, so the cost is invisible to it.
//
// The salt is directory-wide, public, and announced in the manifest. Its job is not
// secrecy — it forces an attacker to redo the work per directory instead of reusing
// one precomputed table. scrypt/argon2id are the one thing WebCrypto cannot do,
// which is exactly why @noble/hashes is a dependency.

import { argon2id } from '@noble/hashes/argon2';
import { scryptAsync } from '@noble/hashes/scrypt';

import { b64uDecode, b64uEncode } from './base64url';
import { utf8 } from './hash';

export interface LookupParams {
  algorithm: string;
  saltB64: string;
  cost: Readonly<Record<string, number>>;
  keyBytes: number;
}

export class EntryKey {
  constructor(readonly bytes: Uint8Array) {}

  /**
   * A key that arrived already derived — the successor named in a signed move.
   * There is no number to hash in that case, and there does not need to be one.
   */
  static fromBase64(value: string): EntryKey {
    return new EntryKey(b64uDecode(value));
  }

  /** The wire form used in `/v1/entry/{key}`: base64url without padding. */
  get base64(): string {
    return b64uEncode(this.bytes);
  }

  /**
   * The section prefix: the leading [sectionBits] bits of the key, rendered as
   * lowercase hex of ceil(sectionBits / 4) digits with unused trailing bits zeroed —
   * the same form the manifest publishes in `snapshot.sections[].prefix`.
   * Ported bit-for-bit from the Dart client; arithmetic (not bitwise) operators on
   * purpose — JS bitwise operators truncate to 32 bits.
   */
  sectionPrefix(sectionBits: number): string {
    if (sectionBits <= 0) {
      return '';
    }
    if (sectionBits > 32) {
      throw new RangeError(`section prefixes above 32 bits are not supported (got ${sectionBits})`);
    }
    const nibbles = Math.ceil(sectionBits / 4);
    let value = 0;
    for (let i = 0; i < nibbles * 4; i += 8) {
      const byte = Math.floor(i / 8);
      value = value * 256 + (byte < this.bytes.length ? this.bytes[byte]! : 0);
    }
    // Drop the bits read past the requested count, then zero the unused low bits of
    // the last nibble so the textual prefix is stable regardless of what follows it.
    const readBits = Math.ceil((nibbles * 4) / 8) * 8;
    value = Math.floor(value / 2 ** (readBits - nibbles * 4));
    const spare = nibbles * 4 - sectionBits;
    value = Math.floor(value / 2 ** spare) * 2 ** spare;
    return value.toString(16).padStart(nibbles, '0');
  }
}

/**
 * Derives the entry key for an E.164 number. Throws when the manifest announces an
 * algorithm this client does not implement — a directory the client cannot derive
 * keys for is a directory it cannot use, and that has to surface as an error rather
 * than as a silent fallback to plaintext numbers.
 */
export async function deriveEntryKey(e164: string, params: LookupParams): Promise<EntryKey> {
  const salt = decodeSalt(params.saltB64);
  const password = utf8(e164);
  switch (params.algorithm) {
    case 'scrypt': {
      const out = await scryptAsync(password, salt, {
        N: params.cost['N'] ?? 16384,
        r: params.cost['r'] ?? 8,
        p: params.cost['p'] ?? 1,
        dkLen: params.keyBytes,
      });
      return new EntryKey(out);
    }
    case 'argon2id': {
      // @noble/hashes ships argon2id synchronously only; one lookup at a time and
      // cost parameters in the tens of milliseconds make that acceptable.
      const out = argon2id(password, salt, {
        t: params.cost['t'] ?? 3,
        m: params.cost['m'] ?? 65536,
        p: params.cost['p'] ?? 1,
        dkLen: params.keyBytes,
      });
      return new EntryKey(out);
    }
    default:
      throw new Error(`Unknown lookup algorithm: ${params.algorithm}`);
  }
}

function decodeSalt(saltB64: string): Uint8Array {
  try {
    return b64uDecode(saltB64);
  } catch {
    throw new Error('Lookup salt is not valid base64url');
  }
}
